import React, { lazy, Suspense } from 'react';
import { Navigate, useLocation, useRoutes } from 'react-router-dom';

import LoadingScreen from 'components/LoadingScreen/LoadingScreen';
import AuthGuard from 'guards/AuthGuard';
import GroupFeatureGuard from 'guards/GroupFeatureGuard';
import GuestGuard from 'guards/GuestGuard';
import RoleGuard from 'guards/RoleGuard';
import DashboardLayout from 'layouts/dashboard/DashboardLayout';
import LogoOnlyLayout from 'layouts/LogoOnlyLayout';

// eslint-disable-next-line react/display-name
const Loadable = Component => props => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { pathname } = useLocation();
    const isDashboard = pathname.includes('/dashboard');

    return (
        <Suspense
            fallback={
                <LoadingScreen
                    sx={{
                        ...(!isDashboard && {
                            top: 0,
                            left: 0,
                            width: 1,
                            zIndex: 9999,
                            position: 'fixed',
                        }),
                    }}
                />
            }
        >
            <Component {...props} />
        </Suspense>
    );
};

// IMPORT COMPONENTS

// Authentication
const Login = Loadable(lazy(() => import('pages/auth/Login/Login')));
const Register = Loadable(lazy(() => import('pages/auth/Register/Register')));
const ResetPassword = Loadable(lazy(() => import('pages/auth/ResetPassword/ResetPassword')));
const VerifyEmailAndCode = Loadable(lazy(() => import('pages/auth/VerifyEmailAndCode/VerifyEmailAndCode')));
const UserManagement = Loadable(lazy(() => import('pages/auth/UserManagement/UserManagement')));

// Dashboard
const Dashboard = Loadable(lazy(() => import('pages/dashboard/Dashboard/Dashboard')));
const Module = Loadable(lazy(() => import('pages/dashboard/Module')));
const Lesson = Loadable(lazy(() => import('pages/dashboard/Lesson/Lesson')));
const Points = Loadable(lazy(() => import('pages/dashboard/Points/Points')));

// Attendance
const Attendance = Loadable(lazy(() => import('pages/dashboard/attendance/List/Attendance')));
const DailySheet = Loadable(lazy(() => import('pages/dashboard/attendance/DailySheet/DailySheet')));
const Report = Loadable(lazy(() => import('pages/dashboard/attendance/Report/Report')));

// Analytics
const Analytics = Loadable(lazy(() => import('pages/dashboard/analytics/List/Groups')));

// Teacher Prizes
const TeacherPrizes = Loadable(lazy(() => import('pages/dashboard/TeacherPrizes/TeacherPrizes')));

// Schedule
const Schedule = Loadable(lazy(() => import('pages/dashboard/Schedule/Schedule')));

// Studies
const AttendanceReport = Loadable(lazy(() => import('pages/dashboard/studies/Attendance/Attendance')));
const StudiesSchedule = Loadable(lazy(() => import('pages/dashboard/studies/Schedule/Schedule')));
const PointsReport = Loadable(lazy(() => import('pages/dashboard/studies/Points/Points')));
const Diploma = Loadable(lazy(() => import('pages/dashboard/studies/diploma/Diploma')));
const Feedback = Loadable(lazy(() => import('pages/dashboard/studies/feedback/Feedback')));

// Management
const Account = Loadable(lazy(() => import('pages/dashboard/Account/Account')));

// Groups
const GroupsList = Loadable(lazy(() => import('pages/dashboard/groups/List/Groups')));
const Group = Loadable(lazy(() => import('pages/dashboard/groups/Group/Group')));
const GroupNew = Loadable(lazy(() => import('pages/dashboard/groups/New/NewGroup')));
const GroupEdit = Loadable(lazy(() => import('pages/dashboard/groups/Edit/EditGroup')));
const GroupContent = Loadable(lazy(() => import('pages/dashboard/GroupContent')));

// Users
const UsersList = Loadable(lazy(() => import('pages/dashboard/users/List/Users')));
const User = Loadable(lazy(() => import('pages/dashboard/users/View/User')));
const UserNew = Loadable(lazy(() => import('pages/dashboard/users/New/NewUser')));
const UserEdit = Loadable(lazy(() => import('pages/dashboard/users/Edit/EditUser')));
const UserChildrenNew = Loadable(lazy(() => import('pages/dashboard/users/Children/NewChild')));

// Children
const ChildrenList = Loadable(lazy(() => import('pages/dashboard/children/List/Children')));
const Child = Loadable(lazy(() => import('pages/dashboard/children/List/Children')));
const ChildNew = Loadable(lazy(() => import('pages/dashboard/children/New/NewChild')));
const ChildEdit = Loadable(lazy(() => import('pages/dashboard/children/Edit/EditChild')));

// Misc
const Page404 = Loadable(lazy(() => import('pages/Page404')));
const Page500 = Loadable(lazy(() => import('pages/Page500')));

export default function Router() {
    return useRoutes([
        {
            path: 'auth',
            children: [
                {
                    path: 'login',
                    element: (
                        <GuestGuard>
                            <Login />
                        </GuestGuard>
                    ),
                },
                {
                    path: 'register',
                    element: (
                        <GuestGuard>
                            <Register />
                        </GuestGuard>
                    ),
                },
                {
                    path: 'reset-password',
                    element: (
                        <GuestGuard>
                            <ResetPassword />
                        </GuestGuard>
                    ),
                },
                {
                    path: 'verify',
                    element: (
                        <AuthGuard>
                            <VerifyEmailAndCode />
                        </AuthGuard>
                    ),
                },
                { path: 'user-management', element: <UserManagement /> },
            ],
        },
        {
            path: 'dashboard',
            element: (
                <AuthGuard>
                    <DashboardLayout />
                </AuthGuard>
            ),
            children: [
                { index: true, element: <Navigate to='/dashboard/modules' replace /> },
                {
                    path: 'modules',
                    element: <Dashboard />,
                },
                {
                    path: 'modules/:module',
                    element: <Module />,
                },
                {
                    path: 'modules/:module/:lesson',
                    children: [
                        { index: true, element: <Lesson /> },
                        { path: ':topic', element: <Lesson /> },
                    ],
                },
                {
                    path: 'points',
                    children: [
                        {
                            index: true,
                            element: (
                                <RoleGuard accessibleRoles={['parent']}>
                                    <Points />
                                </RoleGuard>
                            ),
                        },
                        {
                            path: ':tab',
                            element: (
                                <RoleGuard accessibleRoles={['parent']}>
                                    <Points />
                                </RoleGuard>
                            ),
                        },
                    ],
                },
                {
                    path: 'account',
                    children: [
                        { index: true, element: <Account /> },
                        { path: ':tab', element: <Account /> },
                    ],
                },
                {
                    path: 'schedule',
                    children: [{ index: true, element: <Schedule /> }],
                },
                {
                    path: 'groups',
                    children: [
                        {
                            index: true,
                            element: (
                                <RoleGuard accessibleRoles={['admin', 'teacher']}>
                                    <GroupsList />
                                </RoleGuard>
                            ),
                        },
                        {
                            path: 'new',
                            element: (
                                <RoleGuard accessibleRoles={['admin']}>
                                    <GroupNew />
                                </RoleGuard>
                            ),
                        },
                        {
                            path: ':group',
                            element: (
                                <RoleGuard accessibleRoles={['admin']}>
                                    <Group />
                                </RoleGuard>
                            ),
                        },
                        {
                            path: ':group/edit',
                            element: (
                                <RoleGuard accessibleRoles={['admin']}>
                                    <GroupEdit />
                                </RoleGuard>
                            ),
                        },
                        {
                            path: ':group/content',
                            element: (
                                <RoleGuard accessibleRoles={['admin', 'teacher']}>
                                    <GroupContent />
                                </RoleGuard>
                            ),
                        },
                    ],
                },
                {
                    path: 'users',
                    children: [
                        {
                            index: true,
                            element: (
                                <RoleGuard accessibleRoles={['admin']}>
                                    <UsersList />
                                </RoleGuard>
                            ),
                        },
                        {
                            path: 'new',
                            element: (
                                <RoleGuard accessibleRoles={['admin']}>
                                    <UserNew />
                                </RoleGuard>
                            ),
                        },
                        {
                            path: ':user',
                            element: (
                                <RoleGuard accessibleRoles={['admin']}>
                                    <User />
                                </RoleGuard>
                            ),
                        },
                        {
                            path: ':user/edit',
                            element: (
                                <RoleGuard accessibleRoles={['admin']}>
                                    <UserEdit />
                                </RoleGuard>
                            ),
                        },
                        {
                            path: ':user/children/new',
                            element: (
                                <RoleGuard accessibleRoles={['admin']}>
                                    <UserChildrenNew />
                                </RoleGuard>
                            ),
                        },
                        {
                            path: ':user/children/:child/edit',
                            element: (
                                <RoleGuard accessibleRoles={['admin']}>
                                    <UserChildrenNew />
                                </RoleGuard>
                            ),
                        },
                    ],
                },
                {
                    path: 'children',
                    children: [
                        {
                            index: true,
                            element: (
                                <RoleGuard accessibleRoles={['parent']}>
                                    <ChildrenList />
                                </RoleGuard>
                            ),
                        },
                        {
                            path: 'new',
                            element: (
                                <RoleGuard accessibleRoles={['parent']}>
                                    <ChildNew />
                                </RoleGuard>
                            ),
                        },
                        {
                            path: ':child',
                            element: (
                                <RoleGuard accessibleRoles={['parent']}>
                                    <Child />
                                </RoleGuard>
                            ),
                        },
                        {
                            path: ':child/edit',
                            element: (
                                <RoleGuard accessibleRoles={['parent']}>
                                    <ChildEdit />
                                </RoleGuard>
                            ),
                        },
                    ],
                },
                {
                    path: 'studies',
                    children: [
                        {
                            path: 'attendance',
                            element: (
                                <RoleGuard accessibleRoles={['child']}>
                                    <AttendanceReport />
                                </RoleGuard>
                            ),
                        },
                        {
                            path: 'schedule',
                            element: (
                                <RoleGuard accessibleRoles={['child']}>
                                    <StudiesSchedule />
                                </RoleGuard>
                            ),
                        },
                        {
                            path: 'points',
                            children: [
                                {
                                    index: true,
                                    element: (
                                        <GroupFeatureGuard feature='space_odyssey_enabled'>
                                            <RoleGuard accessibleRoles={['child']}>
                                                <PointsReport />
                                            </RoleGuard>
                                        </GroupFeatureGuard>
                                    ),
                                },
                                {
                                    path: ':tab',
                                    element: (
                                        <GroupFeatureGuard feature='space_odyssey_enabled'>
                                            <RoleGuard accessibleRoles={['child']}>
                                                <PointsReport />
                                            </RoleGuard>
                                        </GroupFeatureGuard>
                                    ),
                                },
                            ],
                        },
                        {
                            path: 'diploma',
                            children: [
                                {
                                    index: true,
                                    element: (
                                        <GroupFeatureGuard feature='diploma_enabled'>
                                            <RoleGuard accessibleRoles={['child', 'parent']}>
                                                <Diploma />
                                            </RoleGuard>
                                        </GroupFeatureGuard>
                                    ),
                                },
                            ],
                        },
                        {
                            path: 'feedback',
                            element: (
                                <GroupFeatureGuard feature='feedback_enabled'>
                                    <RoleGuard accessibleRoles={['child', 'parent']}>
                                        <Feedback />
                                    </RoleGuard>
                                </GroupFeatureGuard>
                            ),
                        },
                    ],
                },
                {
                    path: 'attendance',
                    children: [
                        {
                            index: true,
                            element: (
                                <RoleGuard accessibleRoles={['admin', 'teacher']}>
                                    <Attendance />
                                </RoleGuard>
                            ),
                        },
                        {
                            path: ':group',
                            element: (
                                <RoleGuard accessibleRoles={['admin', 'teacher']}>
                                    <Report />
                                </RoleGuard>
                            ),
                        },
                        {
                            path: ':group/daily-sheet/:date',
                            element: (
                                <RoleGuard accessibleRoles={['admin', 'teacher']}>
                                    <DailySheet />
                                </RoleGuard>
                            ),
                        },
                    ],
                },
                {
                    path: 'analytics',
                    children: [
                        {
                            index: true,
                            element: (
                                <RoleGuard accessibleRoles={['admin']}>
                                    <Analytics />
                                </RoleGuard>
                            ),
                        },
                    ],
                },
                {
                    path: 'teacher-prizes',
                    children: [
                        {
                            index: true,
                            element: (
                                <RoleGuard accessibleRoles={['teacher', 'admin']}>
                                    <TeacherPrizes />
                                </RoleGuard>
                            ),
                        },
                    ],
                },
            ],
        },
        {
            path: '/',
            element: (
                <AuthGuard>
                    <DashboardLayout />
                </AuthGuard>
            ),
            children: [{ index: true, element: <Navigate to='/dashboard' replace /> }],
        },
        {
            path: '*',
            element: <LogoOnlyLayout />,
            children: [
                // { path: 'maintenance', element: <Maintenance /> },
                { path: '404', element: <Page404 /> },
                { path: '500', element: <Page500 /> },
                { path: '*', element: <Navigate to='/404' replace /> },
            ],
        },
        { path: '*', element: <Navigate to='/404' replace /> },
    ]);
}
